// components/Admin/RolesManager.jsx
import React, { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import { Save, Loader, Shield, AlertCircle, RefreshCw, User } from 'lucide-react'
import { supabase } from '../../lib/supabaseClient'

const RolesManager = () => {
  const [users, setUsers] = useState([])
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(null)
  const [currentUserRole, setCurrentUserRole] = useState(null)
  const [currentUser, setCurrentUser] = useState(null)
  const [error, setError] = useState('')
  const [initialized, setInitialized] = useState(false)

  useEffect(() => {
    console.log('🔄 RolesManager - useEffect initial')
    initializeComponent()
  }, [])

  const initializeComponent = async () => {
    console.log('🚀 RolesManager - Initialisation...')
    setLoading(true)
    setError('')
    
    try {
      // 1. Récupérer la session active
      const { data: { session }, error: sessionError } = await supabase.auth.getSession()
      
      console.log('📦 Session:', session)
      
      if (sessionError) {
        console.error('❌ Erreur session:', sessionError)
        throw sessionError
      }

      if (!session) {
        console.log('⚠️ Pas de session active')
        setError('Session expirée. Veuillez vous reconnecter.')
        setLoading(false)
        return
      }

      const user = session.user
      setCurrentUser(user)
      console.log('👤 Utilisateur actuel:', user.email)

      // 2. Récupérer le profil de l'utilisateur
      const { data: profile, error: profileError } = await supabase
        .from('profiles')
        .select('role, first_name, last_name')
        .eq('id', user.id)
        .single()

      if (profileError) {
        console.error('❌ Erreur profil:', profileError)
        setCurrentUserRole('viewer')
        setError('Erreur lors de la récupération du profil')
        setLoading(false)
        return
      }

      console.log('👔 Profil récupéré:', profile)
      const userRole = profile?.role || 'viewer'
      setCurrentUserRole(userRole)

      // 3. Vérifier les permissions
      if (userRole !== 'super_admin') {
        console.log('🚫 Accès refusé - Rôle:', userRole)
        setError(`Accès refusé : vous devez être Super Admin pour gérer les rôles. Votre rôle actuel : ${userRole}`)
        setLoading(false)
        return
      }

      // 4. Charger tous les utilisateurs
      console.log('✅ Super Admin confirmé - Chargement des utilisateurs...')
      await fetchUsers()
      setInitialized(true)

    } catch (err) {
      console.error('❌ Erreur initialisation:', err)
      setError(err.message || 'Erreur lors de l\'initialisation')
    } finally {
      setLoading(false)
    }
  }

  const fetchUsers = async () => {
    console.log('📥 Chargement des utilisateurs...')
    try {
      const { data: profiles, error: profilesError } = await supabase
        .from('profiles')
        .select('id, email, first_name, last_name, role, genre, domain, specialite, company_name, created_at')
        .order('created_at', { ascending: false })

      if (profilesError) {
        console.error('❌ Erreur chargement profiles:', profilesError)
        throw profilesError
      }

      console.log('✅ Utilisateurs chargés:', profiles?.length || 0)
      console.log('📋 Données:', profiles)
      setUsers(profiles || [])
      setError('')
    } catch (error) {
      console.error('❌ Erreur fetchUsers:', error)
      setError('Erreur lors du chargement des utilisateurs')
      throw error
    }
  }

  const handleRoleChange = async (userId, newRole) => {
    console.log(`🔄 Changement de rôle pour ${userId} vers ${newRole}`)
    setSaving(userId)
    setError('')
    
    try {
      if (currentUserRole !== 'super_admin') {
        throw new Error('Permissions insuffisantes')
      }

      const { error } = await supabase
        .from('profiles')
        .update({ role: newRole })
        .eq('id', userId)

      if (error) throw error

      setUsers(prev => prev.map(user => 
        user.id === userId ? { ...user, role: newRole } : user
      ))

      console.log('✅ Rôle mis à jour avec succès')
      
      const successMsg = document.createElement('div')
      successMsg.className = 'fixed top-4 right-4 bg-green-600 text-white px-6 py-3 rounded-lg shadow-lg z-50 flex items-center gap-2'
      successMsg.innerHTML = `
        <svg class="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
          <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd"/>
        </svg>
        <span>Rôle mis à jour avec succès</span>
      `
      document.body.appendChild(successMsg)
      setTimeout(() => successMsg.remove(), 3000)

    } catch (error) {
      console.error('❌ Erreur changement rôle:', error)
      setError(error.message || 'Erreur lors de la mise à jour du rôle')
    } finally {
      setSaving(null)
    }
  }

  const handleRefresh = async () => {
    console.log('🔄 Rafraîchissement manuel...')
    setLoading(true)
    try {
      await fetchUsers()
    } catch (error) {
      console.error('❌ Erreur rafraîchissement:', error)
    } finally {
      setLoading(false)
    }
  }

  const getFullName = (user) => {
    if (user.first_name && user.last_name) {
      return `${user.first_name} ${user.last_name}`
    }
    if (user.first_name) return user.first_name
    if (user.last_name) return user.last_name
    if (user.username) return user.username
    return 'Non défini'
  }

  const roleDescriptions = {
    super_admin: 'Accès complet, gère les rôles et toutes les actualités',
    admin: 'Gère toutes les actualités (Vente + Auto)',
    formateur: 'Crée et modifie ses propres actualités',
    viewer: 'Accès lecture seule'
  }

  const roleColors = {
    super_admin: 'bg-red-600/20 text-red-400 border-red-600',
    admin: 'bg-purple-600/20 text-purple-400 border-purple-600',
    formateur: 'bg-blue-600/20 text-blue-400 border-blue-600',
    viewer: 'bg-gray-600/20 text-gray-400 border-gray-600'
  }

  // État de chargement
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-slate-900">
        <div className="text-center">
          <Loader className="w-12 h-12 animate-spin text-blue-500 mx-auto mb-4" />
          <p className="text-gray-400">Chargement des données...</p>
          {currentUser && (
            <p className="text-sm text-gray-500 mt-2">Connecté : {currentUser.email}</p>
          )}
        </div>
      </div>
    )
  }

  // Accès refusé
  if (error && currentUserRole !== 'super_admin') {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center p-6">
        <div className="bg-red-900/20 border border-red-600 rounded-lg p-8 max-w-md">
          <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-white mb-2 text-center">Accès Refusé</h2>
          <p className="text-red-300 mb-4 text-center">{error}</p>
          
          <div className="bg-slate-800 rounded-lg p-4 space-y-2">
            {currentUser && (
              <div className="text-sm text-gray-400">
                <p className="flex items-center gap-2">
                  <User className="w-4 h-4" />
                  <span className="text-white">{currentUser.email}</span>
                </p>
              </div>
            )}
            <div className="text-sm text-gray-400">
              <p>Rôle actuel : 
                <span className={`ml-2 font-semibold ${roleColors[currentUserRole || 'viewer'].split(' ')[1]}`}>
                  {(currentUserRole || 'viewer').toUpperCase().replace('_', ' ')}
                </span>
              </p>
            </div>
          </div>

          <button
            onClick={() => window.location.href = '/'}
            className="mt-6 w-full bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg transition-colors"
          >
            Retour à l'accueil
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-slate-900 p-6">
      <div className="max-w-7xl mx-auto space-y-6">
        {/* En-tête */}
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div className="flex items-center gap-3">
            <div className="bg-blue-600 p-3 rounded-lg">
              <Shield className="w-8 h-8 text-white" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-white">Gestion des Rôles</h1>
              <p className="text-gray-400">Administration des permissions utilisateurs</p>
              {currentUser && (
                <p className="text-xs text-gray-500 mt-1">Connecté : {currentUser.email}</p>
              )}
            </div>
          </div>
          <button
            onClick={handleRefresh}
            disabled={loading}
            className="bg-blue-600 hover:bg-blue-700 disabled:bg-gray-600 disabled:cursor-not-allowed text-white px-4 py-2 rounded-lg flex items-center gap-2 transition-colors"
          >
            <RefreshCw className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
            Actualiser
          </button>
        </div>

        {/* Messages d'erreur */}
        {error && currentUserRole === 'super_admin' && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-red-900/20 border border-red-600 rounded-lg p-4 flex items-center gap-3"
          >
            <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0" />
            <p className="text-red-300">{error}</p>
          </motion.div>
        )}

        {/* Info rôles */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {Object.entries(roleDescriptions).map(([role, desc]) => (
            <motion.div
              key={role}
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              className={`rounded-lg p-4 border ${roleColors[role]}`}
            >
              <p className={`text-sm font-semibold mb-2 ${roleColors[role].split(' ')[1]}`}>
                {role.toUpperCase().replace('_', ' ')}
              </p>
              <p className="text-xs text-gray-400">{desc}</p>
            </motion.div>
          ))}
        </div>

        {/* Tableau utilisateurs */}
        <div className="bg-slate-800/60 rounded-lg overflow-hidden border border-slate-700">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-slate-700/50 border-b border-slate-600">
                  <th className="px-6 py-4 text-left text-sm font-semibold text-gray-300">Email</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-gray-300">Nom complet</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-gray-300">Genre</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-gray-300">Domaine</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-gray-300">Créé le</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-gray-300">Rôle actuel</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-gray-300">Modifier</th>
                </tr>
              </thead>
              <tbody>
                {users.map((user) => {
                  const role = user.role || 'viewer'
                  return (
                    <motion.tr
                      key={user.id}
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      className="border-b border-slate-700 hover:bg-slate-700/30 transition-colors"
                    >
                      <td className="px-6 py-4 text-white font-medium">
                        <div className="flex items-center gap-2">
                          <User className="w-4 h-4 text-gray-400" />
                          {user.email}
                        </div>
                      </td>
                      <td className="px-6 py-4 text-gray-300">
                        <div>
                          <p className="font-medium">{getFullName(user)}</p>
                          {user.company_name && (
                            <p className="text-xs text-gray-500">{user.company_name}</p>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-400">
                        {user.genre ? (
                          <span className="px-2 py-1 bg-slate-700 rounded text-xs">
                            {user.genre === 'Formateur' ? '👨‍🏫' : '👩‍🏫'} {user.genre}
                          </span>
                        ) : (
                          <span className="text-gray-600">-</span>
                        )}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-400">
                        {user.domain && user.specialite ? (
                          <div className="text-xs">
                            <p className="text-white">{user.domain}</p>
                            <p className="text-gray-500">{user.specialite}</p>
                          </div>
                        ) : (
                          <span className="text-gray-600">-</span>
                        )}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-400">
                        {new Date(user.created_at).toLocaleDateString('fr-FR', {
                          day: '2-digit',
                          month: 'short',
                          year: 'numeric'
                        })}
                      </td>
                      <td className="px-6 py-4">
                        <span className={`text-xs font-semibold px-3 py-1.5 rounded-full border ${roleColors[role]}`}>
                          {role.replace('_', ' ').toUpperCase()}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-2">
                          <select
                            value={role}
                            onChange={(e) => handleRoleChange(user.id, e.target.value)}
                            disabled={saving === user.id}
                            className="bg-slate-700 border border-slate-600 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            <option value="viewer">Viewer</option>
                            <option value="formateur">Formateur</option>
                            <option value="admin">Admin</option>
                            <option value="super_admin">Super Admin</option>
                          </select>
                          {saving === user.id && (
                            <Loader className="w-5 h-5 animate-spin text-blue-400" />
                          )}
                        </div>
                      </td>
                    </motion.tr>
                  )
                })}
              </tbody>
            </table>
          </div>

          {users.length === 0 && !loading && (
            <div className="text-center text-gray-400 py-12">
              <Shield className="w-12 h-12 mx-auto mb-3 opacity-50" />
              <p>Aucun utilisateur trouvé</p>
            </div>
          )}
        </div>

        {/* Note informative */}
        <div className="bg-blue-900/20 border border-blue-600/30 rounded-lg p-4">
          <p className="text-sm text-blue-300">
            <strong>Note :</strong> Les nouveaux utilisateurs ont le rôle "Viewer" par défaut. 
            Seuls les Super Admins peuvent modifier les rôles. La modification prend effet immédiatement.
          </p>
        </div>

        {/* Statistiques */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {Object.keys(roleDescriptions).map(role => {
            const count = users.filter(u => u.role === role).length
            return (
              <div key={role} className={`rounded-lg p-4 border ${roleColors[role]}`}>
                <p className="text-2xl font-bold text-white mb-1">{count}</p>
                <p className={`text-xs ${roleColors[role].split(' ')[1]}`}>
                  {role.replace('_', ' ').toUpperCase()}
                </p>
              </div>
            )
          })}
        </div>
      </div>
    </div>
  )
}

export default RolesManager